import { useState } from "react"
import {
  CircleAlert,
  Download,
  FileSpreadsheet,
  Filter,
  PackageCheck,
  RefreshCw,
  TriangleAlert,
  UploadCloud,
} from "lucide-react"
import type { ReactNode } from "react"
import {
  LIVE_OFFER_REBUILD_DEFAULT_FLOOR,
  liveOfferRebuildFloorSchema,
  type MarketplaceId,
} from "@nova-ai/shared"

import {
  rebuildOf,
  rebuildSubmissionReceiptOf,
  storageRefreshOf,
  useBaselinkerRefresh,
  type MiraklRebuildReport,
  type OctopiaRebuildReport,
  type OctopiaRebuildRow,
  type RebuildCsv,
  type RebuildReport,
  type RebuildRow,
  type RebuildSupport,
  type RebuildUpload,
  type RebuildVerification,
  type RefreshAvailability,
  type RefreshJob,
  type RefreshMode,
  type RefreshReport,
  type RefreshSelection,
} from "../../services/use-baselinker-refresh"
import { formatDateTime, formatRelativeTime } from "../../lib/format-date"
import { cn } from "../../lib/utils"
import { Callout } from "../ui/callout"
import { Modal } from "../ui/modal"
import { SearchInput } from "../ui/search-input"
import { StatusBadge } from "../ui/status-badge"
import { Table, type TableColumn } from "../ui/table"

// The panel storage refresh, per shop. This does NOT set operator-supplied
// values: it triggers Baselinker's own bulk operation that copies the assigned
// storage's price / quantity onto the live marketplace offers. So the control says
// "from storage", and the preview pairs each number with what it should be — the
// live marketplace QTY against the storage QTY replacing it, and the live
// Baselinker price against Magento's regular price (never the promo price — a
// Magento discount does not reach a marketplace listing). The
// "Only differences" filter keeps just the rows where one of those pairs
// disagrees, which is the whole point on an account of a few thousand offers.
//
// Preview and Sync are two separate buttons on one control row. Preview is
// read-only and reports what a sync would change (the same values the CLI's
// dry-run prints); Sync starts the panel bulk operation, after a confirmation.
// Offers whose storage QTY the panel did not expose are skipped by a quantity
// sync rather than blocking it, and the count is reported before and after.
//
// The second control row is a DIFFERENT operation with a different source. It
// reads every offer straight from the marketplace, pushes quantity ≤ the chosen
// floor as 0 and leaves every higher quantity exactly as it is. The reviewed
// artifact is uploaded only on platforms where that capability is activated.
// It touches no Baselinker anything.
// Same job and same one-run-per-shop rule, so the two can never run at once: they
// write the same live offers.

// One operation per run: QTY or price, never both. The API still accepts the
// combined `price-quantity` mode (the CLI uses it), but running two bulk
// operations behind one click makes the outcome harder to read — the panel
// reports them separately and only the QTY half can be verified. Offering the
// combination is a later decision.
const MODES: { value: RefreshMode; label: string; hint: string }[] = [
  {
    value: "quantity",
    label: "QTY",
    hint: "Quantity from storage — verified by re-reading the offers afterwards.",
  },
  {
    value: "price",
    label: "Price",
    hint: "Price from storage. The panel cannot show the expected storage price, so this can only be confirmed as accepted.",
  },
]

/** Rows drawn from the FULL selection — a sample, because a whole account can
 *  hold thousands of offers. The rest are counted rather than drawn, and the count
 *  is stated. */
const PREVIEW_ROW_LIMIT = 50

/** Rows drawn when the differences filter is on. Much higher than the sample cap:
 *  the differing set is the actionable one and is normally small (a couple of
 *  hundred on a 2,500-offer account), and a cap low enough to hide most of it made
 *  the filter look like it had done nothing — the first rows of an account are
 *  often differing ones, so the visible top of the table did not change. */
const DIFF_ROW_LIMIT = 500

/** How many blocked/mismatched offers are named inline. Enough to act on; the
 *  count always states the whole, and the downloaded file is the full record. */
const BLOCKED_ROW_LIMIT = 20

export function BaselinkerRefreshSection({ marketplace }: { marketplace: MarketplaceId }) {
  const refresh = useBaselinkerRefresh(marketplace)
  const { availability } = refresh
  const support = availability.data?.support

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <div className="flex flex-col gap-3 border-b border-border/70 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <PackageCheck className="size-4.5" aria-hidden="true" />
          </span>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-medium text-foreground">Baselinker storage refresh</h2>

              {support?.supported === true ? (
                <StatusBadge tone="muted" size="sm">
                  {`${support.service}/${support.site} · account ${support.accountId}`}
                </StatusBadge>
              ) : null}
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              Two ways to move this shop&rsquo;s live offers: Sync copies the assigned Baselinker
              storage&rsquo;s price or quantity onto them; Rebuild reads the marketplace itself and
              applies the stock floor. Previews read only.
            </p>
          </div>
        </div>

        {refresh.lastFinishedAt !== null ? (
          <span
            className="shrink-0 text-xs text-muted-foreground"
            title={formatDateTime(refresh.lastFinishedAt)}
          >
            Last run {formatRelativeTime(refresh.lastFinishedAt)}
          </span>
        ) : null}
      </div>

      <SectionBody refresh={refresh} />
    </section>
  )
}

/** The card's body: the four states that carry no controls, then the working
 *  surface. Split out of the section so each state is an early return rather than
 *  a stack of nested conditionals. */
function SectionBody({ refresh }: { refresh: ReturnType<typeof useBaselinkerRefresh> }) {
  const { availability } = refresh
  const support = availability.data?.support

  if (availability.isLoading) {
    return (
      <div className="p-5" aria-busy="true">
        <div className="h-16 animate-pulse rounded-lg bg-muted/60" />
      </div>
    )
  }
  if (availability.error) {
    return (
      <Message
        icon={<CircleAlert className="size-4" aria-hidden="true" />}
        title="Refresh availability could not be read"
        detail="Nova could not reach the API for this shop. Try again in a moment."
      />
    )
  }
  const rebuild = availability.data?.rebuild ?? {
    supported: false as const,
    reason: "Rebuild availability could not be read.",
  }
  const syncBlocked = syncBlockedReason(availability.data?.panelConfigured, support)
  if (syncBlocked !== null && !rebuild.supported) {
    return <Message title="Neither control is available for this shop" detail={syncBlocked} />
  }
  return <RefreshWorkbench refresh={refresh} syncBlocked={syncBlocked} rebuild={rebuild} />
}

/** Why Sync cannot run for this shop, or null when it can.
 *
 *  A shop the panel cannot reach can still be rebuilt from Mirakl, so an
 *  unavailable Sync half no longer takes the whole card down — it disables its own
 *  controls and says why, right where they are. */
function syncBlockedReason(
  panelConfigured: boolean | undefined,
  support: RefreshAvailability["support"] | undefined
): string | null {
  if (panelConfigured === false) {
    return "Panel credentials (BL_EMAIL + BL_PASSWORD) are unset on this API, so no shop can be synced from storage."
  }
  return support?.supported === false ? support.reason : null
}

/** The working surface: mode, the Preview/Sync pair, and whatever the followed
 *  run has to report. Separate from the unavailable states above so each stays
 *  one flat read. */
function RefreshWorkbench({
  refresh,
  syncBlocked,
  rebuild,
}: {
  refresh: ReturnType<typeof useBaselinkerRefresh>
  /** Why Sync cannot run here, or null when it can. */
  syncBlocked: string | null
  /** Whether Rebuild can run here, or why not. */
  rebuild: RebuildSupport
}) {
  const { availability, job, isRunning, isStalled, start, dismiss } = refresh
  const [mode, setMode] = useState<RefreshMode>("quantity")
  const [floorInput, setFloorInput] = useState(String(LIVE_OFFER_REBUILD_DEFAULT_FLOOR))
  const floorResult = parseFloorInput(floorInput)
  // Both writes ask first — they change live marketplace offers and nothing here
  // can undo either. The previews don't: they only read.
  const [confirming, setConfirming] = useState<"sync" | "upload" | null>(null)

  const isBusy = isRunning || start.isPending
  // A quantity sync fails closed when any selected offer's storage QTY could not
  // be read. We only know that from a preview of the SAME mode — so the block
  // applies then, and otherwise the server's own refusal is what surfaces.
  const skipped = skippedCountFor(job, mode)
  // Which review an upload would send. Only a FINISHED rebuild preview on screen
  // is one: the upload sends its exact bytes, so without it there is nothing
  // approved to send and the button stays off.
  const reviewed = reviewedRebuildOf(job)
  const rebuildControls = rebuildControlState(rebuild, isBusy, reviewed !== null)
  return (
    <div className="space-y-4 p-5">
      <RunControls
        mode={mode}
        onModeChange={setMode}
        syncDisabled={syncBlocked !== null || isBusy}
        rebuildDisabled={rebuildControls.previewDisabled}
        uploadDisabled={rebuildControls.uploadDisabled}
        isBusy={isBusy}
        zeroAtOrBelow={reviewed?.report.zeroAtOrBelow ?? null}
        floorInput={floorInput}
        floorError={floorResult.error}
        onFloorChange={setFloorInput}
        onPreview={() => start.mutate({ mode, phase: "preview" })}
        onSync={() => setConfirming("sync")}
        onRebuild={() => {
          if (floorResult.value !== null) {
            start.mutate({
              mode: "offer-feed",
              phase: "preview",
              zeroAtOrBelow: floorResult.value,
            })
          }
        }}
        onUpload={() => setConfirming("upload")}
      />

      <WorkbenchConfirm
        confirming={confirming}
        mode={mode}
        support={availability.data?.support}
        scope={previewScopeFor(job, mode)}
        reviewed={reviewed}
        onCancel={() => setConfirming(null)}
        onConfirmSync={() => {
          setConfirming(null)
          start.mutate({ mode, phase: "commit" })
        }}
        onConfirmUpload={(reviewedJobId) => {
          setConfirming(null)
          start.mutate({ mode: "offer-feed", phase: "commit", reviewedJobId })
        }}
      />

      <WorkbenchNotices
        skippedCount={skipped}
        selected={storageRefreshOf(job)?.selected ?? 0}
        error={start.error?.message ?? null}
        isStalled={isStalled}
        syncBlocked={syncBlocked}
        rebuildBlocked={rebuildControls.previewBlocked}
        uploadBlocked={rebuildControls.uploadBlocked}
      />

      {job ? <RunState job={job} isRunning={isRunning} onDismiss={dismiss} /> : null}
    </div>
  )
}

export function rebuildControlState(rebuild: RebuildSupport, isBusy: boolean, hasReview: boolean) {
  return {
    previewDisabled: !rebuild.supported || isBusy,
    previewBlocked: rebuild.supported ? null : rebuild.reason,
    uploadDisabled: !rebuild.supported || !rebuild.upload.supported || isBusy || !hasReview,
    uploadBlocked: rebuild.supported && !rebuild.upload.supported ? rebuild.upload.reason : null,
  }
}

/** Exported for the dashboard tests: this is the rule the control enforces. */
export function parseFloorInput(input: string): { value: number | null; error: string | null } {
  if (!/^\d+$/.test(input)) {
    return { value: null, error: "Enter a whole number from 0 to 1,000,000." }
  }
  const parsed = liveOfferRebuildFloorSchema.safeParse(Number(input))
  if (!parsed.success) {
    return { value: null, error: parsed.error.issues[0]?.message ?? "Invalid stock floor." }
  }
  return { value: parsed.data, error: null }
}

/** Whichever confirmation is open, or nothing. Split out of the workbench so each
 *  dialog's own props stay beside it rather than inflating one render. */
function WorkbenchConfirm({
  confirming,
  mode,
  support,
  scope,
  reviewed,
  onCancel,
  onConfirmSync,
  onConfirmUpload,
}: {
  confirming: "sync" | "upload" | null
  mode: RefreshMode
  support: RefreshAvailability["support"] | undefined
  scope: RefreshReport | null
  reviewed: { jobId: string; report: RebuildReport } | null
  onCancel: () => void
  onConfirmSync: () => void
  onConfirmUpload: (reviewedJobId: string) => void
}) {
  if (confirming === "sync") {
    return (
      <SyncConfirm
        mode={mode}
        support={support}
        scope={scope}
        onCancel={onCancel}
        onConfirm={onConfirmSync}
      />
    )
  }
  // An upload confirmation with no review would be asking about bytes that do not
  // exist, so it simply does not open.
  if (confirming === "upload" && reviewed !== null) {
    return (
      <RebuildUploadConfirm
        marketplace={reviewed.report.marketplace}
        scope={reviewed.report}
        onCancel={onCancel}
        onConfirm={() => onConfirmUpload(reviewed.jobId)}
      />
    )
  }
  return null
}

/** What a sync of `mode` would touch, as far as we can honestly say: only a
 *  preview of the SAME mode describes it. A stale one from the other mode would
 *  overstate what we know, so it counts as no scope at all. */
function previewScopeFor(job: RefreshJob | null, mode: RefreshMode): RefreshReport | null {
  const report = storageRefreshOf(job)
  return job?.phase === "preview" && job.mode === mode ? report : null
}

/** The reviewed rebuild an upload would send, with the job id that holds its exact
 *  bytes — or null when there is nothing reviewed on screen.
 *
 *  Only a FINISHED preview counts. A running one has not reported yet, and a
 *  committed report is not a review: its upload already happened. Uploading
 *  without one is refused by the API too; this just keeps the button honest. */
function reviewedRebuildOf(
  job: RefreshJob | null
): { jobId: string; report: RebuildReport } | null {
  const report = rebuildOf(job)
  if (job === null || report === null || job.phase !== "preview" || job.status !== "done") {
    return null
  }
  return { jobId: job.id, report }
}

/** A report from a build older than skip-instead-of-refuse has no `skipped` at
 *  all — `result` is persisted jsonb, so those rows keep that shape forever. The
 *  API normalises them, and this is the second belt for a browser running ahead
 *  of the API mid-deploy. */
function skippedOf(
  report: RefreshReport | null | undefined
): NonNullable<RefreshReport["skipped"]> {
  return report?.skipped ?? []
}

/** How many offers a quantity sync would leave alone, as far as the last preview
 *  of the same mode can say. Price skips nothing — it never reads storage QTY. */
function skippedCountFor(job: RefreshJob | null, mode: RefreshMode): number {
  if (mode !== "quantity" || job?.mode !== "quantity") {
    return 0
  }
  return skippedOf(storageRefreshOf(job)).length
}

/** Everything the workbench has to say before the report itself: why Sync is
 *  refusing, a failed start, a run that stopped reporting. Kept beside the
 *  controls rather than below the table — a disabled control whose reason is
 *  off-screen reads as a dead button, which is exactly how it read. */
function WorkbenchNotices({
  skippedCount,
  selected,
  error,
  isStalled,
  syncBlocked,
  rebuildBlocked,
  uploadBlocked,
}: {
  skippedCount: number
  selected: number
  error: string | null
  isStalled: boolean
  /** Why Sync is off for this shop, or null. */
  syncBlocked: string | null
  /** Why Rebuild is off for this shop, or null. */
  rebuildBlocked: string | null
  /** Why Upload is off while read-only rebuilds remain available, or null. */
  uploadBlocked: string | null
}) {
  return (
    <>
      {syncBlocked !== null ? (
        <div role="status">
          <Callout tone="info" dense title="Sync from storage is unavailable here">
            {syncBlocked}
          </Callout>
        </div>
      ) : null}

      {rebuildBlocked !== null ? (
        <div role="status">
          <Callout tone="info" dense title="Live-offer rebuilding is unavailable here">
            {rebuildBlocked}
          </Callout>
        </div>
      ) : null}

      {uploadBlocked !== null ? (
        <div role="status">
          <Callout tone="info" dense title="Live upload is not activated here">
            {`${uploadBlocked} Read-only rebuild previews remain available.`}
          </Callout>
        </div>
      ) : null}

      {skippedCount > 0 ? (
        <div role="status">
          <Callout tone="warning" dense title="Some offers will be skipped">
            {`${skippedCount} of the ${selected.toLocaleString()} selected offers show no storage QTY on their listing row, so a quantity sync has nothing to verify them against. It runs on the other ${(selected - skippedCount).toLocaleString()} and leaves these alone — they are marked "unknown" in the table below. Assign their storage in Baselinker to include them.`}
          </Callout>
        </div>
      ) : null}

      {error !== null ? (
        <div role="status">
          <Callout tone="destructive" dense>
            {error}
          </Callout>
        </div>
      ) : null}

      {isStalled ? (
        <div role="status">
          <Callout tone="warning" dense>
            The previous run stopped reporting and is treated as dead. Starting a new preview
            replaces it.
          </Callout>
        </div>
      ) : null}
    </>
  )
}

/** Exported for its own test — the one control that starts a live write should
 *  have its "no write without an explicit yes" property pinned directly.
 *
 *  The last gate before a live write. Names the shop, the operation, and — when
 *  a preview of the same mode is on screen — exactly how many offers it will
 *  touch, so "yes" is answered against numbers rather than a vague warning.
 *
 *  Deliberately does NOT require a preview: an operator who knows what they are
 *  doing can sync straight away. Without one the dialog says so, rather than
 *  implying a scope it cannot know. */
export function SyncConfirm({
  mode,
  support,
  scope,
  onCancel,
  onConfirm,
}: {
  mode: RefreshMode
  support: RefreshAvailability["support"] | undefined
  scope: RefreshReport | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const shop =
    support?.supported === true
      ? `${support.service}/${support.site} (account ${support.accountId})`
      : "this shop"
  const operation = mode === "quantity" ? "quantity" : "price"
  return (
    // `max-w-lg`, not `w-…`: the shell already sets `w-full`, and a competing
    // width class loses to it — which is what stretched this dialog edge to edge.
    <Modal label="Confirm Baselinker sync" onClose={onCancel} surfaceClassName="max-w-lg">
      <div className="flex gap-3 border-b border-border/70 px-5 py-4">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-warning/10 text-warning">
          <TriangleAlert className="size-4.5" aria-hidden="true" />
        </span>

        <div>
          <h2 className="text-sm font-medium text-foreground">
            {`Sync ${operation} to live offers?`}
          </h2>

          <p className="mt-1 text-xs text-muted-foreground">
            This writes to offers that are live on the marketplace right now, and nothing here can
            undo it.
          </p>
        </div>
      </div>

      <div className="space-y-3 px-5 py-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
          <dt className="text-muted-foreground">Shop</dt>

          <dd className="text-right font-medium text-foreground">{shop}</dd>

          <dt className="text-muted-foreground">Operation</dt>

          <dd className="text-right font-medium text-foreground">
            {mode === "quantity" ? "Quantity from storage" : "Price from storage"}
          </dd>

          <dt className="text-muted-foreground">Offers affected</dt>

          <dd className="text-right font-medium tabular-nums text-foreground">
            {/* Minus what a quantity sync skips — the dialog must state the
                number that actually gets written, not the selection size. */}
            {scope ? (scope.selected - skippedOf(scope).length).toLocaleString() : "unknown"}
          </dd>

          {scope && skippedOf(scope).length > 0 ? (
            <>
              <dt className="text-muted-foreground">Skipped</dt>

              <dd className="text-right font-medium tabular-nums text-warning">
                {`${skippedOf(scope).length.toLocaleString()} · no storage QTY`}
              </dd>
            </>
          ) : null}
        </dl>

        {scope ? (
          <p className="border-t border-border/70 pt-3 text-xs text-muted-foreground">
            {mode === "quantity"
              ? "Afterwards the offers are re-read until their quantity matches storage, so the result is verified."
              : "Baselinker returns an operation id, but the panel cannot show the expected storage price — so this can only be reported as accepted, never verified."}
          </p>
        ) : (
          <div role="status">
            <Callout tone="warning" dense>
              No preview has been run for this mode, so the number of offers this will change is
              unknown. Preview first if you want to see it before writing.
            </Callout>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border/70 bg-muted/[0.18] px-5 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 items-center rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-muted/60"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground shadow-card transition-opacity hover:opacity-90"
        >
          <UploadCloud className="size-3.5" aria-hidden="true" />

          {`Yes, sync ${operation}`}
        </button>
      </div>
    </Modal>
  )
}

function RunControls({
  mode,
  onModeChange,
  syncDisabled,
  rebuildDisabled,
  uploadDisabled,
  isBusy,
  zeroAtOrBelow,
  floorInput,
  floorError,
  onFloorChange,
  onPreview,
  onSync,
  onRebuild,
  onUpload,
}: {
  mode: RefreshMode
  onModeChange: (next: RefreshMode) => void
  syncDisabled: boolean
  rebuildDisabled: boolean
  uploadDisabled: boolean
  isBusy: boolean
  /** The floor the reviewed rebuild applied, when one is on screen. */
  zeroAtOrBelow: number | null
  floorInput: string
  floorError: string | null
  onFloorChange: (next: string) => void
  onPreview: () => void
  onSync: () => void
  onRebuild: () => void
  onUpload: () => void
}) {
  return (
    <div className="space-y-3">
      <BulkOperationControls
        mode={mode}
        onModeChange={onModeChange}
        disabled={syncDisabled}
        isBusy={isBusy}
        onPreview={onPreview}
        onSync={onSync}
      />

      <RebuildControls
        rebuildDisabled={rebuildDisabled}
        uploadDisabled={uploadDisabled}
        isBusy={isBusy}
        zeroAtOrBelow={zeroAtOrBelow}
        floorInput={floorInput}
        floorError={floorError}
        onFloorChange={onFloorChange}
        onRebuild={onRebuild}
        onUpload={onUpload}
      />
    </div>
  )
}

/** The panel bulk operation: pick QTY or price, preview it, run it. */
function BulkOperationControls({
  mode,
  onModeChange,
  disabled,
  isBusy,
  onPreview,
  onSync,
}: {
  mode: RefreshMode
  onModeChange: (next: RefreshMode) => void
  disabled: boolean
  isBusy: boolean
  onPreview: () => void
  onSync: () => void
}) {
  const active = MODES.find((option) => option.value === mode)
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="radiogroup"
        aria-label="Refresh mode"
        className="inline-flex rounded-full border border-border bg-muted/30 p-0.5"
      >
        {MODES.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === mode}
            title={option.hint}
            onClick={() => onModeChange(option.value)}
            className={cn(
              "h-7 rounded-full px-3 text-xs font-medium transition-colors",
              option.value === mode
                ? "bg-card text-foreground shadow-card"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onPreview}
        disabled={disabled}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-muted/60 disabled:opacity-60"
      >
        <RefreshCw className={cn("size-3.5", isBusy && "animate-spin")} aria-hidden="true" />

        {isBusy ? "Working…" : "Preview"}
      </button>

      {/* Sync is the write. Kept beside Preview rather than inside the preview
          report so the two actions read as a pair. It is never disabled for
          unverifiable offers any more — the sync skips those and runs on the
          rest, and the count is reported above. */}
      <button
        type="button"
        onClick={onSync}
        disabled={disabled}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground shadow-card transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        <UploadCloud className="size-3.5" aria-hidden="true" />
        Sync
      </button>

      {active ? <span className="text-xs text-muted-foreground">{active.hint}</span> : null}
    </div>
  )
}

/** Read this shop's live offers, apply the stock floor, then review and upload
 *  where the marketplace capability is active. Upload only lights up once a
 *  rebuild has reported, because it sends that review's exact artifact. */
export function RebuildControls({
  rebuildDisabled,
  uploadDisabled,
  isBusy,
  zeroAtOrBelow,
  floorInput,
  floorError,
  onFloorChange,
  onRebuild,
  onUpload,
}: {
  rebuildDisabled: boolean
  uploadDisabled: boolean
  isBusy: boolean
  zeroAtOrBelow: number | null
  floorInput: string
  floorError: string | null
  onFloorChange: (next: string) => void
  onRebuild: () => void
  onUpload: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border/70 pt-3">
      <div className="flex flex-col gap-1 text-xs font-medium text-foreground">
        {/* The correction sits OUTSIDE the label: wrapped in it, the message
            would become part of the field's own name. */}
        <label htmlFor="rebuild-stock-floor">Stock floor</label>

        <input
          id="rebuild-stock-floor"
          type="number"
          min={0}
          max={1_000_000}
          step={1}
          required
          value={floorInput}
          onChange={(event) => onFloorChange(event.target.value)}
          aria-invalid={floorError !== null}
          aria-describedby={floorError === null ? undefined : "rebuild-stock-floor-error"}
          // Never disabled by its OWN error: a rejected value has to stay
          // editable, or the operator is locked out of correcting it.
          disabled={rebuildDisabled}
          className="h-9 w-32 rounded-lg border border-border bg-card px-3 text-sm tabular-nums text-foreground shadow-card disabled:opacity-60"
        />

        <span id="rebuild-stock-floor-error" className="font-normal text-destructive">
          {floorError ?? ""}
        </span>
      </div>

      <button
        type="button"
        onClick={onRebuild}
        disabled={rebuildDisabled || floorError !== null}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-muted/60 disabled:opacity-60"
      >
        <FileSpreadsheet className={cn("size-3.5", isBusy && "animate-pulse")} aria-hidden="true" />
        Rebuild live offers
      </button>

      <button
        type="button"
        onClick={onUpload}
        disabled={uploadDisabled}
        title={
          uploadDisabled
            ? "A finished review and an activated marketplace upload capability are required."
            : "Upload the exact reviewed rebuild artifact."
        }
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-4 text-sm font-medium text-warning shadow-card transition-colors hover:bg-warning/20 disabled:opacity-50"
      >
        <UploadCloud className="size-3.5" aria-hidden="true" />
        Upload reviewed rebuild
      </button>

      <span className="text-xs text-muted-foreground">
        {`Reads every live offer straight from the marketplace. The only requested change is quantity: ${zeroAtOrBelow === null ? `quantity ≤ ${floorError === null && floorInput !== "" ? floorInput : "the stock floor"}` : `quantity ≤ ${zeroAtOrBelow}`} becomes 0, so those offers go dark; every higher quantity stays unchanged and uncapped. Review the exact artifact before upload.`}
      </span>
    </div>
  )
}

/** What the followed run is doing or found. Reporting only — the actions live in
 *  the control row above, so Preview and Sync read as a pair. */
function RunState({
  job,
  isRunning,
  onDismiss,
}: {
  job: RefreshJob
  isRunning: boolean
  onDismiss: () => void
}) {
  const submissionReceipt = rebuildSubmissionReceiptOf(job)
  const submissionReceiptMessage = submissionReceipt
    ? ` Octopia package ${submissionReceipt.packageId} was already submitted from review ${submissionReceipt.reviewedJobId}. Do not retry this review.`
    : ""
  if (isRunning) {
    return (
      <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="size-3.5 animate-spin" aria-hidden="true" />

        {runningMessage(job)}
      </div>
    )
  }
  if (job.status === "error") {
    return (
      <div role="status">
        <Callout tone="destructive" dense title="The refresh failed">
          {`${job.error ?? "No reason was recorded."}${submissionReceiptMessage}`}
        </Callout>
      </div>
    )
  }
  if (job.status === "interrupted") {
    return (
      <div role="status">
        <Callout tone="warning" dense>
          {`${interruptedMessage(job)}${submissionReceiptMessage}`}
        </Callout>
      </div>
    )
  }
  const rebuild = rebuildOf(job)
  if (rebuild) {
    return <RebuildReportView report={rebuild} onDismiss={onDismiss} />
  }
  if (job.mode === "offer-feed") {
    return (
      <div role="status">
        <Callout tone="info" dense>
          This rebuild ran on an older build of Nova, so its report cannot be read here. Run a fresh
          rebuild to review and upload.
        </Callout>
      </div>
    )
  }
  const refresh = storageRefreshOf(job)
  if (!refresh) {
    return null
  }
  return refresh.committed ? (
    <CommittedReport report={refresh} />
  ) : (
    <PreviewReport report={refresh} onDismiss={onDismiss} />
  )
}

/** What the run is doing right now, in its own terms — the three jobs wait on
 *  very different things, and "this can take a while" is only honest if it says
 *  what it is waiting for. */
function runningMessage(job: RefreshJob): string {
  if (job.mode === "offer-feed") {
    return job.phase === "preview"
      ? "Reading every live offer and building the reviewed quantity change set. A full catalog walk takes a few minutes."
      : "Re-reading live offers to check the review, then submitting and verifying it. This can take a while."
  }
  return job.phase === "preview"
    ? "Reading this account's ongoing offers…"
    : "Bulk operation running — waiting for the offers to match storage. This can take a while."
}

/** What a dead run leaves behind. Only a commit can have written anything, and
 *  where to check it differs per mode. */
function interruptedMessage(job: RefreshJob): string {
  if (job.phase === "preview") {
    return "The API restarted while the preview was running. Nothing was written; run it again."
  }
  return job.mode === "offer-feed"
    ? "The API restarted while the rebuild was being submitted. The marketplace may already be processing it — check its package or import before re-running."
    : "The API restarted while the bulk operation was running. Baselinker may still have applied it — check the operation in the panel before re-running."
}

/** Exported for its own test: the differences filter is the one piece of this card
 *  with interactive state, and driving it through the whole section would mean
 *  standing up a query client and a fake API just to click one button. */
export function PreviewReport({
  report,
  onDismiss,
}: {
  report: RefreshReport
  onDismiss: () => void
}) {
  const [onlyDiffs, setOnlyDiffs] = useState(false)
  const [search, setSearch] = useState("")
  // Filtering happens over the WHOLE selection, before the row cap — the point of
  // both filters is to find the handful of offers you care about among thousands,
  // which capping first would hide.
  const differing = report.selectedOffers.filter(rowDiffers)
  const source = (onlyDiffs ? differing : report.selectedOffers).filter((row) =>
    matchesSearch(search, row.sku, row.id, row.name)
  )
  const rows = source.slice(0, rowLimitFor({ onlyDiffs, search }))
  const hidden = source.length - rows.length
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground">
        <span className="font-medium">
          {`Selected ${report.selected.toLocaleString()} of ${report.offers.toLocaleString()} ongoing offers`}
        </span>

        <span className="text-xs text-muted-foreground">
          {`Planned: ${report.planned.join(" → ")}`}
        </span>

        <SearchInput
          size="sm"
          value={search}
          onChange={setSearch}
          placeholder="Search SKU, offer id, name"
          ariaLabel="Search offers"
          className="ml-auto w-56"
        />

        <button
          type="button"
          onClick={() => setOnlyDiffs((current) => !current)}
          aria-pressed={onlyDiffs}
          title="Keep only offers where the marketplace QTY differs from storage, or the Baselinker price differs from Magento's."
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
            onlyDiffs
              ? "border-warning/40 bg-warning/10 text-warning"
              : "border-border bg-card text-muted-foreground hover:text-foreground"
          )}
        >
          <Filter className="size-3" aria-hidden="true" />

          {onlyDiffs
            ? `Showing differences only (${differing.length.toLocaleString()})`
            : `Only differences (${differing.length.toLocaleString()})`}
        </button>
      </div>

      <Table
        columns={PREVIEW_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        empty={emptyMessageFor({
          search,
          onlyDiffs,
          noMatchOfDiffs: "Every selected offer already matches storage QTY and Magento's price.",
          noRows: "No ongoing offers were selected.",
        })}
        embedded
        dense
        cellAlign="middle"
      />

      {hidden > 0 ? (
        <p className="text-xs text-muted-foreground">
          {`Showing the first ${rows.length.toLocaleString()} of ${source.length.toLocaleString()} matching offers — a sync applies to all ${report.selected.toLocaleString()} selected, not just the rows on screen.`}
        </p>
      ) : null}

      {report.planned.includes("price") ? (
        <Callout tone="info" dense>
          The panel does not expose the expected storage price on an offer row, so a price sync can
          only be reported as accepted, never verified.
        </Callout>
      ) : null}

      <button
        type="button"
        onClick={onDismiss}
        className="text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        Clear this preview
      </button>
    </div>
  )
}

function CommittedReport({ report }: { report: RefreshReport }) {
  const unverified = report.jobs.some(
    ({ verification }) => verification.kind === "operation-accepted-unverified"
  )
  return (
    <div className="space-y-3">
      <div role="status">
        <Callout
          tone={unverified ? "warning" : "success"}
          dense
          title={
            unverified
              ? "Refresh submitted — the price half is unverified"
              : "Refresh verified by re-reading the offers"
          }
        >
          <ul className="space-y-1">
            {report.jobs.map((operation) => (
              <li key={operation.operationId}>
                <span className="font-medium">{operation.kind}</span>

                {` operation ${operation.operationId}: `}

                {operation.verification.kind === "offer-reread"
                  ? `${operation.verification.offers.toLocaleString()} offers now match storage QTY (the panel's own progress status can lag behind this).`
                  : `accepted for ${operation.verification.offers.toLocaleString()} offers — check this operation id in Baselinker.`}
              </li>
            ))}
          </ul>
        </Callout>
      </div>
    </div>
  )
}

/** Free-text match over an offer's identifying fields. Case-insensitive
 *  substring; an empty query matches everything, so the box starts as a no-op. */
function matchesSearch(query: string, ...fields: (string | null)[]): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === "") {
    return true
  }
  return fields.some((field) => (field ?? "").toLowerCase().includes(needle))
}

/** How many rows to draw. A filtered set is the actionable one and is normally
 *  small, so it gets the high cap; an unfiltered account gets the sample cap. */
function rowLimitFor({ onlyDiffs, search }: { onlyDiffs: boolean; search: string }): number {
  return onlyDiffs || search.trim() !== "" ? DIFF_ROW_LIMIT : PREVIEW_ROW_LIMIT
}

/** Why a table is empty — a search that matched nothing reads very differently
 *  from a filter that found nothing to fix, and both differ from no rows at all. */
function emptyMessageFor(input: {
  search: string
  onlyDiffs: boolean
  noMatchOfDiffs: string
  noRows: string
}): string {
  if (input.search.trim() !== "") {
    return `No offer matches “${input.search.trim()}”.`
  }
  return input.onlyDiffs ? input.noMatchOfDiffs : input.noRows
}

/** The rebuilt file: the totals, then every row's before/after. Read-only — the
 *  actions live in the control row above.
 *
 *  The default view is the changing rows: an operator running this is looking for
 *  the offers whose quantity moves, not for a list of 2,400 untouched ones.
 *
 *  Exported for its own test, like the preview table: the default filter and the
 *  search are the interactive state worth pinning directly. */
export function RebuildReportView({
  report,
  onDismiss,
}: {
  report: RebuildReport
  onDismiss: () => void
}) {
  return report.kind === "octopia-live-offer-rebuild" ? (
    <OctopiaRebuildReportView report={report} onDismiss={onDismiss} />
  ) : (
    <MiraklRebuildReportView report={report} onDismiss={onDismiss} />
  )
}

function MiraklRebuildReportView({
  report,
  onDismiss,
}: {
  report: MiraklRebuildReport
  onDismiss: () => void
}) {
  // Pre-filter to the changed rows only when there ARE any. On an already-floored
  // shop the filter would otherwise open on an empty table for a run that
  // succeeded, which reads as a failure — the whole point of the review surface is
  // seeing the rows the upload carries.
  const [onlyChanges, setOnlyChanges] = useState(() => report.rows.some((row) => row.changes))
  const [search, setSearch] = useState("")
  const changing = report.rows.filter((row) => row.changes)
  const source = (onlyChanges ? changing : report.rows).filter((row) =>
    matchesSearch(search, row.sku, row.name, row.productId)
  )
  const rows = source.slice(0, rowLimitFor({ onlyDiffs: onlyChanges, search }))
  const hidden = source.length - rows.length
  return (
    <div className="space-y-3">
      {report.committed ? (
        <>
          <RebuildUploadOutcome upload={report.upload} />

          <RebuildVerificationNote verification={report.verification} />
        </>
      ) : null}

      <RebuildTotalsLine report={report} />

      <RebuildAtFloorNote report={report} />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground">
        <SearchInput
          size="sm"
          value={search}
          onChange={setSearch}
          placeholder="Search SKU, name, EAN"
          ariaLabel="Search rebuilt offers"
          className="w-56"
        />

        <DownloadCsvButton csv={report.csv} />

        <button
          type="button"
          onClick={() => setOnlyChanges((current) => !current)}
          aria-pressed={onlyChanges}
          title="Keep only offers whose quantity this file changes."
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
            onlyChanges
              ? "border-warning/40 bg-warning/10 text-warning"
              : "border-border bg-card text-muted-foreground hover:text-foreground"
          )}
        >
          <Filter className="size-3" aria-hidden="true" />

          {`Quantity changes (${changing.length.toLocaleString()})`}
        </button>
      </div>

      <Table
        columns={REBUILD_COLUMNS}
        rows={rows}
        rowKey={(row) => row.sku}
        empty={emptyMessageFor({
          search,
          onlyDiffs: onlyChanges,
          noMatchOfDiffs:
            "No offer changes quantity — every rebuilt row carries the quantity the marketplace already has.",
          noRows: "The rebuild produced no offer rows.",
        })}
        embedded
        dense
        cellAlign="middle"
      />

      {hidden > 0 ? (
        <p className="text-xs text-muted-foreground">
          {`Showing the first ${rows.length.toLocaleString()} of ${source.length.toLocaleString()} matching rows — the file carries all ${report.rowCount.toLocaleString()}.`}
        </p>
      ) : null}

      <RebuildBlockedRows report={report} />

      {report.committed ? null : (
        <button
          type="button"
          onClick={onDismiss}
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          Clear this rebuild
        </button>
      )}
    </div>
  )
}

/** Octopia's reviewed JSON is a list of strict quantity-only Update requests.
 *  The table exposes the identity tuple used to prove the review is still
 *  current, while the requested quantity stays visibly separate. */
function OctopiaRebuildReportView({
  report,
  onDismiss,
}: {
  report: OctopiaRebuildReport
  onDismiss: () => void
}) {
  const [onlyChanges, setOnlyChanges] = useState(() => report.rows.some((row) => row.changes))
  const [search, setSearch] = useState("")
  const changing = report.rows.filter((row) => row.changes)
  const source = (onlyChanges ? changing : report.rows).filter((row) =>
    matchesSearch(
      search,
      row.sellerExternalReference,
      row.offerId,
      row.productGtin,
      row.productReference
    )
  )
  const rows = source.slice(0, rowLimitFor({ onlyDiffs: onlyChanges, search }))
  const hidden = source.length - rows.length
  return (
    <div className="space-y-3">
      {report.committed ? (
        <>
          <OctopiaPackageOutcome result={report.package} />

          <OctopiaVerificationNote verification={report.verification} />
        </>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground">
        <span className="font-medium">
          {`${report.totals.read.toLocaleString()} live offers read from Octopia`}
        </span>

        <span className="text-xs text-muted-foreground">
          {`${report.totals.zeroed.toLocaleString()} set to 0 (quantity ≤ ${report.zeroAtOrBelow}) · ${report.totals.alreadyZero.toLocaleString()} already 0 · ${report.totals.unchanged.toLocaleString()} left unchanged · ${report.totals.blocked.toLocaleString()} blocked`}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        {`${report.rowCount.toLocaleString()} exact JSON Update requests. Each request carries only sellerExternalReference and quantity.`}
      </p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground">
        <SearchInput
          size="sm"
          value={search}
          onChange={setSearch}
          placeholder="Search seller ref, offer id, GTIN"
          ariaLabel="Search rebuilt offers"
          className="w-64"
        />

        <DownloadArtifactButton artifact={report.artifact} />

        <button
          type="button"
          onClick={() => setOnlyChanges((current) => !current)}
          aria-pressed={onlyChanges}
          title="Keep only offers whose quantity this rebuild changes."
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
            onlyChanges
              ? "border-warning/40 bg-warning/10 text-warning"
              : "border-border bg-card text-muted-foreground hover:text-foreground"
          )}
        >
          <Filter className="size-3" aria-hidden="true" />

          {`Quantity changes (${changing.length.toLocaleString()})`}
        </button>
      </div>

      <Table
        columns={OCTOPIA_REBUILD_COLUMNS}
        rows={rows}
        rowKey={(row) => row.sellerExternalReference}
        empty={emptyMessageFor({
          search,
          onlyDiffs: onlyChanges,
          noMatchOfDiffs:
            "No offer changes quantity — every request carries the quantity Octopia already has.",
          noRows: "The rebuild produced no offer rows.",
        })}
        embedded
        dense
        cellAlign="middle"
      />

      {hidden > 0 ? (
        <p className="text-xs text-muted-foreground">
          {`Showing the first ${rows.length.toLocaleString()} of ${source.length.toLocaleString()} matching rows — the artifact carries all ${report.rowCount.toLocaleString()}.`}
        </p>
      ) : null}

      <OctopiaBlockedRows report={report} />

      {report.retention.state === "omitted" ? (
        <Callout tone="warning" dense title="Rebuild details were too large to retain">
          {`${report.retention.bytes.toLocaleString()} bytes exceeded the ${report.retention.maxBytes.toLocaleString()}-byte persisted result budget. Counts remain available. Nova omitted ${report.retention.omitted.rows.toLocaleString()} reviewed rows, ${report.retention.omitted.packageOffers.toLocaleString()} package outcomes, and ${report.retention.omitted.verificationMismatches.toLocaleString()} verification mismatches.`}
        </Callout>
      ) : null}

      {report.committed ? null : (
        <button
          type="button"
          onClick={onDismiss}
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          Clear this rebuild
        </button>
      )}
    </div>
  )
}

/** The four buckets every offer read falls into, plus the blocked count. Stated as
 *  a line rather than folded into one row total: "2,400 rows" hides whether the
 *  run switched 40 offers off or 2,000. */
function RebuildTotalsLine({ report }: { report: MiraklRebuildReport }) {
  const { totals } = report
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground">
      <span className="font-medium">
        {`${totals.read.toLocaleString()} live offers read from Mirakl`}
      </span>

      <span className="text-xs text-muted-foreground">
        {`${totals.zeroed.toLocaleString()} set to 0 (quantity ≤ ${report.zeroAtOrBelow}) · ${totals.alreadyZero.toLocaleString()} already 0 · ${totals.unchanged.toLocaleString()} left unchanged · ${totals.blocked.toLocaleString()} blocked`}
      </span>

      {report.header.length > 0 ? (
        <span
          className="text-xs text-muted-foreground/60"
          title="The OF01 columns the rebuilt file carries."
        >
          {report.header.join(" · ")}
        </span>
      ) : null}
    </div>
  )
}

/** Why a rebuild that changes nothing is the RIGHT answer, in the operator's own
 *  terms: the shop is already at the floor.
 *
 *  A complete-snapshot rebuild of an already-floored shop reports `0 set to 0`
 *  next to thousands read, which reads as "it did nothing" rather than "there was
 *  nothing to do". This says which is true, from the rows themselves — every
 *  surviving offer is above the threshold, and the ones at or under it already sit
 *  at 0. The inactive count comes from the live offer's own active flag, so it
 *  claims only what Mirakl reported.
 *
 *  Renders only for a run that read offers and changed none of them. */
function RebuildAtFloorNote({ report }: { report: MiraklRebuildReport }) {
  const { totals } = report
  if (totals.read === 0 || totals.zeroed > 0 || report.rows.length === 0) {
    return null
  }
  const atZero = report.rows.filter((row) => row.quantityAfter === 0)
  const inactive = atZero.filter((row) => !row.activeNow).length
  const zeroDetail =
    inactive === atZero.length
      ? "all inactive on Mirakl"
      : `${inactive.toLocaleString()} of them inactive on Mirakl`
  return (
    <p className="text-xs text-muted-foreground">
      {`Already at the floor — no offer is stocked between 1 and ${report.zeroAtOrBelow}. `}

      {atZero.length > 0 ? `${atZero.length.toLocaleString()} sit at 0 (${zeroDetail}) and ` : ""}

      {`${totals.unchanged.toLocaleString()} carry more than ${report.zeroAtOrBelow}. Uploading this file would leave every quantity exactly as the marketplace has it.`}
    </p>
  )
}

/** Download the exact file the upload sends. The bytes come from the report, not
 *  from the table above — a CSV rebuilt in the browser could differ from what
 *  Mirakl receives, which is the one thing this button must not do.
 *
 *  Renders nothing when the run did not keep the file (too large for the job
 *  row); the API refuses to upload such a review, so the pair stays consistent. */
function DownloadCsvButton({ csv }: { csv: RebuildCsv | null }) {
  if (!csv) {
    return null
  }
  const download = () => {
    // `text/csv` + a Blob URL, revoked right after the click — nothing is kept.
    const url = URL.createObjectURL(new Blob([csv.text], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = csv.filename
    link.click()
    URL.revokeObjectURL(url)
  }
  return (
    <button
      type="button"
      onClick={download}
      title={`Download ${csv.filename} — the exact file the upload sends to Mirakl.`}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <Download className="size-3" aria-hidden="true" />
      Download CSV
    </button>
  )
}

/** Download Octopia's exact reviewed JSON. The table is capped for rendering;
 * the download always uses the retained server artifact, never browser-built rows. */
function DownloadArtifactButton({ artifact }: { artifact: OctopiaRebuildReport["artifact"] }) {
  if (!artifact) {
    return null
  }
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([artifact.text], { type: "application/json;charset=utf-8" })
    )
    const link = document.createElement("a")
    link.href = url
    link.download = artifact.filename
    link.click()
    URL.revokeObjectURL(url)
  }
  return (
    <button
      type="button"
      onClick={download}
      title={`Download ${artifact.filename} — the exact reviewed artifact sent to Octopia.`}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <Download className="size-3" aria-hidden="true" />
      Download JSON
    </button>
  )
}

/** The offers the rebuild could NOT speak for. Every one is named, because every
 *  one blocks the upload: a file missing them would leave those offers at their
 *  current quantity while reporting a complete run. */
function RebuildBlockedRows({ report }: { report: MiraklRebuildReport }) {
  if (report.blocked.length === 0) {
    return null
  }
  return (
    <Callout
      tone="warning"
      dense
      title={`${report.blocked.length.toLocaleString()} offers block this upload`}
    >
      <p>
        A rebuild speaks for the whole shop, so the upload is refused until every offer has a
        complete row. Fix these on Mirakl, then rebuild.
      </p>

      <ul className="mt-1 space-y-1">
        {report.blocked.slice(0, BLOCKED_ROW_LIMIT).map((blocked) => (
          <li key={`${blocked.sku}:${blocked.field}`}>
            <span className="font-medium">{blocked.sku}</span>

            {` — ${blocked.field}: ${blocked.reason}`}
          </li>
        ))}
      </ul>

      {report.blocked.length > BLOCKED_ROW_LIMIT ? (
        <p className="mt-1">
          {`and ${(report.blocked.length - BLOCKED_ROW_LIMIT).toLocaleString()} more.`}
        </p>
      ) : null}
    </Callout>
  )
}

function OctopiaBlockedRows({ report }: { report: OctopiaRebuildReport }) {
  if (report.blocked.length === 0) {
    return null
  }
  return (
    <Callout
      tone="warning"
      dense
      title={`${report.blocked.length.toLocaleString()} offers block this upload`}
    >
      <p>
        The review must identify every live offer and exactly one Seller quantity. Fix these in
        Octopia, then rebuild.
      </p>

      <ul className="mt-1 space-y-1">
        {report.blocked.slice(0, BLOCKED_ROW_LIMIT).map((blocked, index) => (
          <li
            key={`${blocked.sellerExternalReference ?? "missing"}:${blocked.offerId ?? "missing"}:${blocked.field}:${index}`}
          >
            <span className="font-medium">
              {blocked.sellerExternalReference ?? blocked.offerId ?? "Unidentified offer"}
            </span>

            {` — ${blocked.field}: ${blocked.reason}`}
          </li>
        ))}
      </ul>

      {report.blocked.length > BLOCKED_ROW_LIMIT ? (
        <p className="mt-1">
          {`and ${(report.blocked.length - BLOCKED_ROW_LIMIT).toLocaleString()} more.`}
        </p>
      ) : null}
    </Callout>
  )
}

function OctopiaPackageOutcome({ result }: { result: OctopiaRebuildReport["package"] }) {
  if (!result) {
    return (
      <div role="status">
        <Callout tone="warning" dense>
          The rebuild committed but recorded no Octopia package. Check the package log before
          re-running.
        </Callout>
      </div>
    )
  }
  const tone = octopiaPackageTone(result)
  return (
    <div role="status">
      <Callout
        tone={tone}
        dense
        title={
          result.terminal
            ? `Octopia package ${result.packageId}: ${result.status ?? "finished"}`
            : `Octopia package ${result.packageId} submitted — outcome not terminal`
        }
      >
        {result.terminal
          ? `${result.integrated.toLocaleString()} offers integrated, ${result.rejected.toLocaleString()} rejected.`
          : "The package reached Octopia, but Nova did not observe a terminal per-offer result. Check this package before retrying."}
      </Callout>
    </div>
  )
}

function octopiaPackageTone(
  result: NonNullable<OctopiaRebuildReport["package"]>
): "warning" | "success" | "info" {
  if (result.rejected > 0) {
    return "warning"
  }
  return result.terminal ? "success" : "info"
}

function OctopiaVerificationNote({
  verification,
}: {
  verification: OctopiaRebuildReport["verification"]
}) {
  if (verification === null) {
    return null
  }
  if (verification.kind === "unavailable") {
    return (
      <div role="status">
        <Callout tone="info" dense title="Not verified by an Octopia re-read">
          {verification.reason}
        </Callout>
      </div>
    )
  }
  if (verification.kind === "verified") {
    return (
      <div role="status">
        <Callout tone="success" dense title="Verified by re-reading Octopia">
          {`All ${verification.checked.toLocaleString()} reviewed offers now carry the requested quantity, across ${verification.offersRead.toLocaleString()} live offers.`}
        </Callout>
      </div>
    )
  }
  return (
    <div role="status">
      <Callout
        tone="warning"
        dense
        title={`${verification.mismatches.length.toLocaleString()} quantities did not land`}
      >
        <ul className="space-y-1">
          {verification.mismatches.slice(0, BLOCKED_ROW_LIMIT).map((mismatch) => (
            <li key={mismatch.sellerExternalReference}>
              <span className="font-medium">{mismatch.sellerExternalReference}</span>

              {` — expected ${mismatch.expected}, Octopia reads ${mismatch.actual ?? "no such offer"}`}
            </li>
          ))}
        </ul>

        {verification.mismatches.length > BLOCKED_ROW_LIMIT ? (
          <p className="mt-1">
            {`and ${(verification.mismatches.length - BLOCKED_ROW_LIMIT).toLocaleString()} more.`}
          </p>
        ) : null}
      </Callout>
    </div>
  )
}

/** What Mirakl did with the uploaded file. An import still being processed is
 *  reported as submitted — the upload landed either way. */
function RebuildUploadOutcome({ upload }: { upload: RebuildUpload | null }) {
  if (!upload) {
    return (
      <div role="status">
        <Callout tone="warning" dense>
          The rebuild committed but recorded no Mirakl import — check the shop&rsquo;s offer imports
          before re-running.
        </Callout>
      </div>
    )
  }
  return (
    <div role="status">
      <Callout
        tone={uploadTone(upload)}
        dense
        title={
          upload.terminal
            ? `Mirakl import ${upload.importId}: ${upload.status ?? "finished"}`
            : `Mirakl import ${upload.importId} submitted — still processing`
        }
      >
        {upload.terminal
          ? `${(upload.linesInSuccess ?? 0).toLocaleString()} rows accepted, ${(upload.linesInError ?? 0).toLocaleString()} rejected${upload.hasErrorReport ? " — an error report is available in the shop's offer imports." : "."}`
          : "The file reached Mirakl; its per-line result is not in yet. Check this import id in the shop's offer imports."}
      </Callout>
    </div>
  )
}

/** The fresh read after the import. This is the only line that can say the shop
 *  actually moved, so an unavailable re-read is reported as unknown, never as a
 *  pass. */
function RebuildVerificationNote({ verification }: { verification: RebuildVerification | null }) {
  if (verification === null) {
    return null
  }
  if (verification.kind === "unavailable") {
    return (
      <div role="status">
        <Callout tone="info" dense title="Not verified by a re-read">
          {verification.reason}
        </Callout>
      </div>
    )
  }
  if (verification.kind === "verified") {
    return (
      <div role="status">
        <Callout tone="success" dense title="Verified by re-reading Mirakl">
          {`All ${verification.checked.toLocaleString()} uploaded rows now carry the quantity the file asked for, across ${verification.offersRead.toLocaleString()} live offers.`}
        </Callout>
      </div>
    )
  }
  return (
    <div role="status">
      <Callout
        tone="warning"
        dense
        title={`${verification.mismatches.length.toLocaleString()} rows did not land`}
      >
        <ul className="space-y-1">
          {verification.mismatches.slice(0, BLOCKED_ROW_LIMIT).map((mismatch) => (
            <li key={mismatch.sku}>
              <span className="font-medium">{mismatch.sku}</span>

              {` — expected ${mismatch.expected}, Mirakl reads ${mismatch.actual ?? "no such offer"}`}
            </li>
          ))}
        </ul>

        {verification.mismatches.length > BLOCKED_ROW_LIMIT ? (
          <p className="mt-1">
            {`and ${(verification.mismatches.length - BLOCKED_ROW_LIMIT).toLocaleString()} more.`}
          </p>
        ) : null}
      </Callout>
    </div>
  )
}

/** Rejected rows are the finding, so they win over "finished"; an import still
 *  running is neither good nor bad news yet. */
function uploadTone(upload: RebuildUpload): "warning" | "success" | "info" {
  if ((upload.linesInError ?? 0) > 0) {
    return "warning"
  }
  return upload.terminal ? "success" : "info"
}

const REBUILD_COLUMNS: readonly TableColumn<RebuildRow>[] = [
  {
    key: "sku",
    header: "Offer",
    render: (row) => (
      <div>
        <div className="font-medium text-foreground">{row.sku}</div>

        <div className="mt-0.5 text-[11px] text-muted-foreground">{row.name ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "quantityBefore",
    header: "QTY now",
    align: "right",
    headerHint: "The quantity this offer carries on Mirakl right now.",
    render: (row) => <Numeric value={row.quantityBefore} />,
  },
  {
    key: "quantityAfter",
    header: "QTY in file",
    align: "right",
    headerHint:
      "The quantity the rebuilt row carries: 0 at or below the floor, otherwise the live quantity unchanged and uncapped. Mirakl derives active/inactive from it.",
    render: (row) => (
      <span
        className={cn("tabular-nums", row.changes ? "text-warning" : "font-medium text-foreground")}
      >
        {row.quantityAfter}
      </span>
    ),
  },
  {
    key: "changes",
    header: "Changes",
    headerHint: "Whether this row moves the offer's quantity.",
    render: (row) =>
      row.changes ? (
        <span className="text-warning">{`${row.quantityBefore} → ${row.quantityAfter}`}</span>
      ) : (
        <span className="text-muted-foreground">no</span>
      ),
  },
  {
    key: "price",
    header: "Price",
    align: "right",
    headerHint: "The offer's live price, carried through unchanged.",
    render: (row) => <PriceCell value={row.price} currency={null} strong />,
  },
  {
    key: "productId",
    header: "EAN",
    headerHint: "The offer's live product reference, carried through unchanged.",
    render: (row) => (
      <span className="font-mono text-[11px] text-muted-foreground">{row.productId ?? "—"}</span>
    ),
  },
  {
    key: "state",
    header: "State",
    headerHint: "The offer's live Mirakl condition code, carried through unchanged.",
    render: (row) => <span className="text-muted-foreground">{row.state ?? "—"}</span>,
  },
]

const OCTOPIA_REBUILD_COLUMNS: readonly TableColumn<OctopiaRebuildRow>[] = [
  {
    key: "sellerExternalReference",
    header: "Offer",
    render: (row) => (
      <div>
        <div className="font-medium text-foreground">{row.sellerExternalReference}</div>

        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{row.offerId}</div>
      </div>
    ),
  },
  {
    key: "quantityBefore",
    header: "QTY now",
    align: "right",
    headerHint: "The single Seller supply quantity Octopia reports now.",
    render: (row) => <Numeric value={row.quantityBefore} />,
  },
  {
    key: "quantityAfter",
    header: "QTY requested",
    align: "right",
    headerHint:
      "The only mutable value in the exact Update request: 0 at or below the floor, otherwise unchanged.",
    render: (row) => (
      <span
        className={cn("tabular-nums", row.changes ? "text-warning" : "font-medium text-foreground")}
      >
        {row.quantityAfter}
      </span>
    ),
  },
  {
    key: "changes",
    header: "Changes",
    render: (row) =>
      row.changes ? (
        <span className="text-warning">{`${row.quantityBefore} → ${row.quantityAfter}`}</span>
      ) : (
        <span className="text-muted-foreground">no</span>
      ),
  },
  {
    key: "condition",
    header: "Condition",
    headerHint: "Reviewed identity field. It is not sent in the quantity-only request.",
    render: (row) => <span className="text-muted-foreground">{row.condition}</span>,
  },
  {
    key: "productGtin",
    header: "GTIN",
    headerHint: "Reviewed product identity. It is not sent in the quantity-only request.",
    render: (row) => (
      <span className="font-mono text-[11px] text-muted-foreground">{row.productGtin}</span>
    ),
  },
  {
    key: "productReference",
    header: "Product ref",
    headerHint: "Reviewed product identity. It is not sent in the quantity-only request.",
    render: (row) => (
      <span className="font-mono text-[11px] text-muted-foreground">{row.productReference}</span>
    ),
  },
  {
    key: "vendorUpdatedAt",
    header: "Updated",
    headerHint: "Octopia's vendor timestamp at review time.",
    render: (row) => (
      <span className="text-[11px] text-muted-foreground">
        {formatDateTime(row.vendorUpdatedAt)}
      </span>
    ),
  },
]

/** The last gate before the reviewed artifact reaches the marketplace. Names the
 *  shop and exactly what the review said, so "yes" is answered against what is
 *  on screen.
 *
 *  Only rendered with a review in hand: without one there is nothing approved to
 *  send, and the button that opens this is off. */
export function RebuildUploadConfirm({
  marketplace,
  scope,
  onCancel,
  onConfirm,
}: {
  marketplace: string
  scope: RebuildReport
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Modal label="Confirm offer rebuild upload" onClose={onCancel} surfaceClassName="max-w-lg">
      <div className="flex gap-3 border-b border-border/70 px-5 py-4">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-warning/10 text-warning">
          <TriangleAlert className="size-4.5" aria-hidden="true" />
        </span>

        <div>
          <h2 className="text-sm font-medium text-foreground">
            Upload the reviewed live-offer rebuild?
          </h2>

          <p className="mt-1 text-xs text-muted-foreground">
            This requests the reviewed quantity for every offer, so offers can go dark. Nothing here
            can undo it.
          </p>
        </div>
      </div>

      <div className="space-y-3 px-5 py-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
          <dt className="text-muted-foreground">Shop</dt>

          <dd className="text-right font-medium text-foreground">{marketplace}</dd>

          <dt className="text-muted-foreground">Rows uploaded</dt>

          <dd className="text-right font-medium tabular-nums text-foreground">
            {scope.rowCount.toLocaleString()}
          </dd>

          <dt className="text-muted-foreground">{`Set to 0 (quantity ≤ ${scope.zeroAtOrBelow})`}</dt>

          <dd className="text-right font-medium tabular-nums text-warning">
            {scope.totals.zeroed.toLocaleString()}
          </dd>

          <dt className="text-muted-foreground">Left unchanged</dt>

          <dd className="text-right font-medium tabular-nums text-foreground">
            {`${scope.totals.unchanged.toLocaleString()} · ${scope.totals.alreadyZero.toLocaleString()} already 0`}
          </dd>
        </dl>

        <p className="border-t border-border/70 pt-3 text-xs text-muted-foreground">
          {scope.kind === "mirakl-offer-rebuild"
            ? "These exact OF01 bytes go up in NORMAL mode. Mirakl is re-read first: if quantity, price, condition or product reference moved — or an offer disappeared — Nova refuses the stale review."
            : "These exact JSON Update requests carry only sellerExternalReference and quantity. Octopia is re-read first: if reviewed identity or quantity moved, an offer disappeared, or a new offer appeared, Nova refuses the stale review."}
        </p>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border/70 bg-muted/[0.18] px-5 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 items-center rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-muted/60"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground shadow-card transition-opacity hover:opacity-90"
        >
          <UploadCloud className="size-3.5" aria-hidden="true" />
          Yes, upload the reviewed rebuild
        </button>
      </div>
    </Modal>
  )
}

const PREVIEW_COLUMNS: readonly TableColumn<RefreshSelection>[] = [
  {
    key: "offer",
    header: "Offer",
    render: (row) => (
      <div>
        <div className="font-medium text-foreground">{row.sku || "—"}</div>

        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{row.id}</div>
      </div>
    ),
  },
  {
    key: "name",
    header: "Product",
    render: (row) => <span className="text-muted-foreground">{row.name || "—"}</span>,
  },
  {
    key: "currentQuantity",
    header: "QTY now",
    align: "right",
    headerHint: "The quantity currently live on the marketplace — what the refresh replaces.",
    render: (row) => <Numeric value={row.currentQuantity} />,
  },
  {
    key: "storageQuantity",
    header: "QTY in storage",
    align: "right",
    headerHint: "The assigned storage's quantity — what the offers converge to.",
    render: (row) => <Numeric value={row.storageQuantity} className="text-foreground" />,
  },
  {
    key: "price",
    header: "Price Baselinker",
    align: "right",
    headerHint:
      "The price this offer is live at right now, read off the Baselinker listing row. The number Magento's should agree with.",
    render: (row) => <PriceCell value={row.currentPrice} currency={row.currency} strong />,
  },
  {
    key: "basePrice",
    header: "Price Magento",
    align: "right",
    headerHint:
      "Magento's regular price (products.price) for this shop's store — the authoritative marketplace price, which 'Price Baselinker' should agree with. Deliberately not the promo price: a Magento discount does not reach a marketplace listing.",
    render: (row) =>
      row.inCatalog ? (
        <div className="inline-flex flex-col items-end">
          <PriceCell
            value={row.basePrice}
            currency={row.currency}
            strong
            // Highlighted only when the live Baselinker price disagrees with the
            // marketplace price — the whole point of the column.
            {...(differs(row.currentPrice, row.basePrice) ? { tone: "warn" as const } : {})}
          />

          {/* A live Magento promo, as a note. It is NOT what the offer should
              cost, so it never drives the highlight or the differences filter. */}
          {row.discountedPrice !== null && differs(row.discountedPrice, row.basePrice) ? (
            <span
              className="text-[11px] text-muted-foreground/60"
              title="A Magento promo is live on this product. Promo pricing does not reach the marketplace listing, so the offer is compared against the regular price above."
            >
              {`promo ${row.discountedPrice}`}
            </span>
          ) : null}
        </div>
      ) : (
        <span
          className="text-muted-foreground/40"
          title="This SKU is not in the catalog mirror for this shop's store."
        >
          not in catalog
        </span>
      ),
  },
]

/** True when two decimal-string prices disagree. Compared as strings on purpose
 *  — both sides are already formatted to 2dp, so nothing round-trips through a
 *  float just to be compared. Unknown on either side is not a difference. */
function differs(a: string | null, b: string | null): boolean {
  return a !== null && b !== null && a !== b
}

/** Does this offer disagree with what it should be? Either half counts: the
 *  marketplace QTY differs from the storage QTY a sync would push, or the live
 *  Baselinker price differs from what nova would put on the offer. An unknown on
 *  either side of a comparison is not a difference — it is a gap, reported
 *  separately (see the unreadable-storage callout and "not in catalog"). */
function rowDiffers(row: RefreshSelection): boolean {
  const quantityDiffers =
    row.currentQuantity !== null &&
    row.storageQuantity !== null &&
    row.currentQuantity !== row.storageQuantity
  return quantityDiffers || differs(row.currentPrice, row.basePrice)
}

function PriceCell({
  value,
  currency,
  strong = false,
  tone,
}: {
  value: string | null
  currency: string | null
  strong?: boolean
  tone?: "warn"
}) {
  if (value === null) {
    return <span className="text-muted-foreground/40">—</span>
  }
  return (
    <span
      className={cn(
        "tabular-nums",
        tone === "warn" && "text-warning",
        !tone && (strong ? "font-medium text-foreground" : "text-muted-foreground")
      )}
    >
      {`${value}${currency ? ` ${currency}` : ""}`}
    </span>
  )
}

/** A count the panel could not read is shown as unknown, never as 0 — a missing
 *  storage QTY is exactly the case the API refuses to run a quantity refresh on. */
function Numeric({ value, className }: { value: number | null; className?: string }) {
  if (value === null) {
    return <span className="text-muted-foreground/40">unknown</span>
  }
  return <span className={cn("tabular-nums text-muted-foreground", className)}>{value}</span>
}

function Message({ icon, title, detail }: { icon?: ReactNode; title: string; detail: string }) {
  return (
    <div className="px-5 py-8 text-center">
      <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-foreground">
        {icon}

        {title}
      </div>

      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </div>
  )
}
